/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { ReactElement, useEffect } from "react";

import compact from "lodash/compact";
import filter from "lodash/filter";
import map from "lodash/map";
import some from "lodash/some";

import {
  ComboBox,
  DatePicker,
  Dropdown,
  IComboBoxOption,
  IDropdownOption,
  Stack,
  Text,
} from "@fluentui/react";

import * as EventConstants from "Constants/EventConstants";

import { DayPickerStrings } from "Constants/DateConstants";
import { fieldTypes, FilterValueFieldNames } from "Constants/FilterFields";
import { IKeyText } from "Interfaces/IKeyText";

import ISideBarProps from "./ISideBarProps";
import {
  comboStyles,
  datePickerStyles,
  dropdownStyles,
  filterStackTokens,
  titleStyles,
  ClearDateText,
  SideBarDiv,
} from "./SideBar.styles";

/**
 * Sidebar displays filter search and listing.
 * @FunctionComponent
 * @param {ISideBarProps} props The side bar props.
 * @returns {React.FunctionComponent<ISidebarProps>} The side bar component.
 */
const SideBar: React.FunctionComponent<ISideBarProps> = (props: ISideBarProps): ReactElement => {
  const {
    awardPeriodOptionList,
    awardPeriodLoadError,
    awardKitSkuOptionList,
    awardKitSkuLoadError,
    countryRegionOptionList,
    countryRegionsLoadError,
    filterFields,
    regionsLoadError,
    regionOptionList,
    selectedFilterValues,
    stateProvinceOptionList,
    stateProvincesLoadError,
    industryFocusOptionList,
    industryFocusLoadError,
    technicalExpertiseOptionList,
    technicalExpertiseLoadError,
    technologyAreaOptionList,
    technologyAreaGroupOptionList,
    technologyAreaLoadError,
    clRegionsOptionList,
    activityEventTypesOptionList,
    reviewStatusOptionList,
    activityEventTypesError,
    clRegionsLoadError,
    availableCpmOptionList,
    title,
    urlWithParam,
    addFilterValue,
    removeFilterValue,
    removeFilterProvinceValue,
    removeFilterCountryValue,
    onTechAreaGroupFilterValueChanged,
    updateFilterValue,
    loadStateProvince,
  } = props;

  /**
   * Event triggered with a filter dropdown is changed
   * @param item the item in dropdown selected
   * @param field selected field
   */
  const onChange = ({ item, field }): void => {
    if (item.selected) {
      addFilterValue({ fieldName: field, value: item.key });
      if (
        !!onTechAreaGroupFilterValueChanged &&
        field === FilterValueFieldNames.TECHNOLOGY_AREA_GROUP
      ) {
        // these are the other tech area groups not selected or already selected.
        const otherTechAreaGroupsNotSelected = filter(
          compact(
            map(technologyAreaGroupOptionList, (option) => {
              {
                if (option.key !== item.key) {
                  return { fieldName: field, value: option.key };
                }
              }
            })
          ),
          (y) => !some(selectedFilterValues, (x) => x.value === y.value)
        );
        onTechAreaGroupFilterValueChanged(otherTechAreaGroupsNotSelected);
      }
    } else {
      removeFilterValue({ fieldName: field, value: item.key });
      if (
        !!onTechAreaGroupFilterValueChanged &&
        field === FilterValueFieldNames.TECHNOLOGY_AREA_GROUP
      ) {
        onTechAreaGroupFilterValueChanged([{ fieldName: field, value: item.key }]);
      }
    }
  };

  /**
   * Returns any selected filter value keys for field (if any)
   * @param fieldName field to fetch currently selected values for
   */
  const getSelectedKeys = (fieldName: string): string[] => {
    return filter(selectedFilterValues, (item) => item.fieldName === fieldName).map(
      (item) => item.value
    );
  };

  const getSelectedKey = (fieldName: string): string => {
    if (!!selectedFilterValues?.length) {
      return selectedFilterValues.find((item) => item.fieldName === fieldName)?.value || "";
    }
    return "";
  };

  const onSelectChange = ({ item, field }): void => {
    const selectedKeys = getSelectedKeys(field);
    selectedKeys.forEach((key) => removeFilterValue({ fieldName: field, value: key }));
    if (!!item) {
      addFilterValue({ fieldName: field, value: item.key });
    }
  };

  const datePickerProps = (field: IKeyText): {} => {
    const defaultProps = {
      strings: DayPickerStrings,
      label: field.text,
      placeholder: "Select a Date",
      onSelectDate: (selectedDate): void => {
        updateFilterValue({ fieldName: field.key, value: selectedDate.toString() });
      },
    };
    const selectedKeys = getSelectedKeys(field.key);

    if (selectedKeys.length > 0) defaultProps["value"] = new Date(selectedKeys[0]);
    return defaultProps;
  };

  /**
   * If the user selects “Online” as the only filter under Event Format
   * For pages that do not have an eventformat filter, it should always return false
   * For pages that have an eventformat filter, check whether only the online option was checked
   */
  const whetherOnlineOnlyOnSelected = (): boolean => {
    const formatField = filterFields.filter((item) => item.key === fieldTypes.FORMAT)[0];
    return (
      formatField &&
      getSelectedKeys(formatField.key).includes(EventConstants.EVENT_FORMAT_ONLINE_TEXT) &&
      getSelectedKeys(formatField.key).length === 1
    );
  };

  /**
   * If the user selects “Online” as the only filter under Event Format on change
   */
  const whetherOnlineOnlyOnSelecting = (selectedText?: string, selected?: boolean): boolean => {
    const fieldKey = filterFields.filter((item) => item.key === fieldTypes.FORMAT)[0].key;

    // Whether only the online option remains checked when the options change from checked to unchecked.
    const whetherOnlineLeftOnly =
      !selected &&
      selectedText !== EventConstants.EVENT_FORMAT_ONLINE_TEXT &&
      getSelectedKeys(fieldKey).includes(EventConstants.EVENT_FORMAT_ONLINE_TEXT) &&
      getSelectedKeys(fieldKey).length === 2;

    // Whether only the online option was checked when the options change from unchecked to checked.
    const whetherOnlineSelectOnly =
      selectedText === EventConstants.EVENT_FORMAT_ONLINE_TEXT &&
      getSelectedKeys(fieldKey).length === 0;

    return whetherOnlineLeftOnly || whetherOnlineSelectOnly;
  };

  /**
   * Loads the state/province options based on the singular selected country.
   * @method
   * @param {string[]} selectedCountries The list of previously selected countries.
   * @param {IComboBoxOption} option The combo box option clicked.
   */
  const loadStateProvinceOptionsBySingularSelectedCountry = (
    selectedCountries: string[],
    option?: IComboBoxOption
  ): void => {
    let countryToFetch = "";
    if (option?.selected && selectedCountries.length === 0) {
      countryToFetch = option.text;
    } else {
      const otherCountriesInList = filter(selectedCountries, (sc) => sc !== option?.text);
      countryToFetch =
        !!otherCountriesInList &&
        otherCountriesInList.length === 1 &&
        some(selectedCountries, (sc) => sc === option?.text) // if selected combobox option is the sole member of previously selected countries.
          ? otherCountriesInList[0]
          : "";
    }

    !!countryToFetch && loadStateProvince(countryToFetch);
  };

  /**
   * Add default option and format SKU options
   * @returns options for SKU
   */
  const formatSkuOptions = (): IDropdownOption[] => {
    const skuList: IDropdownOption[] = [];
    const defaultOption = { key: "", text: "Any" };
    skuList.push(defaultOption);

    if (awardKitSkuOptionList && awardKitSkuOptionList.length > 0) {
      awardKitSkuOptionList.map((option: any) => {
        skuList.push({ key: option.sku, text: option.sku });
      });
    }
    return skuList;
  };

  /**
   * MVP Applications can include query parameters.
   * Load state/province options based on the selected country
   * when the page is loaded with a country filter in query parameter.
   */
  useEffect(() => {
    if (urlWithParam) {
      const selectedCountryFilters = selectedFilterValues.filter(
        (item: any) => item.fieldName === fieldTypes.COUNTRY
      );
      if (selectedCountryFilters.length === 1) {
        const selectedCountry = {
          key: selectedCountryFilters[0].value,
          text: selectedCountryFilters[0].value,
          selected: true,
        };
        loadStateProvinceOptionsBySingularSelectedCountry([], selectedCountry);
      }
    }
  }, [urlWithParam, selectedFilterValues]);

  return (
    <SideBarDiv>
      <div className="content">
        <Text styles={titleStyles}>{title || "Filter"}</Text>
        <Stack tokens={filterStackTokens}>
          {filterFields.map((filterField) => {
            const selectedCountries = getSelectedKeys(fieldTypes.COUNTRY);

            switch (filterField.type) {
              case fieldTypes.SELECT:
                const selectOptions: IDropdownOption[] = filterField.options?.map((option) => ({
                  key: option.key,
                  text: option.text,
                })) as IDropdownOption[];

                return (
                  <React.Fragment key={filterField.key}>
                    <Dropdown
                      placeholder="Any"
                      label={filterField.text}
                      selectedKey={getSelectedKey(filterField.key)}
                      onChange={(_, item): void =>
                        onSelectChange({
                          item,
                          field: filterField.key,
                        })
                      }
                      options={selectOptions}
                      styles={dropdownStyles}
                    />
                  </React.Fragment>
                );
              case fieldTypes.MULTI_SELECT:
                const options: IDropdownOption[] = filterField.options?.map((option) => ({
                  key: option.key,
                  text: option.text,
                })) as IDropdownOption[];

                return (
                  <React.Fragment key={filterField.key}>
                    <Dropdown
                      placeholder="Any"
                      label={filterField.text}
                      selectedKeys={getSelectedKeys(filterField.key)}
                      onChange={(_, item): void => onChange({ item, field: filterField.key })}
                      multiSelect
                      options={options}
                      styles={dropdownStyles}
                    />
                  </React.Fragment>
                );
              case fieldTypes.FORMAT:
                const eventFormatOptions: IDropdownOption[] = filterField.options?.map(
                  (option) => ({
                    key: option.key,
                    text: option.text,
                  })
                ) as IDropdownOption[];

                return (
                  <React.Fragment key={filterField.key}>
                    <Dropdown
                      placeholder="Any"
                      label={filterField.text}
                      selectedKeys={getSelectedKeys(filterField.key)}
                      onChange={(_, item): void => {
                        onChange({ item, field: filterField.key });
                        if (whetherOnlineOnlyOnSelecting(item?.text, item?.selected)) {
                          if (removeFilterCountryValue) {
                            removeFilterCountryValue();
                          }
                          if (removeFilterProvinceValue) {
                            removeFilterProvinceValue();
                          }
                        }
                      }}
                      multiSelect
                      options={eventFormatOptions}
                      styles={dropdownStyles}
                    />
                  </React.Fragment>
                );
              case fieldTypes.EXPEDITED:
                const expeditedOptions: IDropdownOption[] = filterField.options?.map((option) => ({
                  key: option.key,
                  text: option.text,
                })) as IDropdownOption[];

                return (
                  <React.Fragment key={filterField.key}>
                    <Dropdown
                      placeholder="Any"
                      label={filterField.text}
                      selectedKeys={getSelectedKeys(filterField.key)}
                      onChange={(_, item): void => {
                        onChange({ item, field: filterField.key });
                      }}
                      multiSelect
                      options={expeditedOptions}
                      styles={dropdownStyles}
                    />
                  </React.Fragment>
                );
              case fieldTypes.SKU:
                return (
                  <React.Fragment key={filterField.key}>
                    <Dropdown
                      placeholder="Any"
                      label={filterField.text}
                      selectedKey={getSelectedKey(filterField.key)}
                      onChange={(_, item): void =>
                        onSelectChange({
                          item,
                          field: filterField.key,
                        })
                      }
                      options={formatSkuOptions()}
                      styles={dropdownStyles}
                      disabled={!!awardKitSkuLoadError && awardKitSkuLoadError.length > 0}
                    />
                  </React.Fragment>
                );
              case fieldTypes.AWARD_PERIOD:
                return (
                  <React.Fragment key={filterField.key}>
                    <ComboBox
                      placeholder="Any"
                      label={filterField.text}
                      selectedKey={getSelectedKeys(filterField.key)}
                      onChange={(_, item): void => {
                        onChange({ item, field: filterField.key });
                      }}
                      multiSelect
                      disabled={!!awardPeriodLoadError && awardPeriodLoadError.length > 0}
                      autoComplete="on"
                      options={awardPeriodOptionList?.map((item) => ({
                        key: item.awardPeriodName,
                        text: item.awardPeriodName,
                      }))}
                      styles={comboStyles}
                    />
                  </React.Fragment>
                );
              // TODO: load countries based on selected region
              case fieldTypes.REGION:
                return (
                  <React.Fragment key={filterField.key}>
                    <ComboBox
                      placeholder="Any"
                      label={filterField.text}
                      selectedKey={getSelectedKeys(filterField.key)}
                      onChange={(_, item): void => {
                        onChange({ item, field: filterField.key });
                      }}
                      multiSelect
                      disabled={regionsLoadError.length > 0 || whetherOnlineOnlyOnSelected()}
                      autoComplete="on"
                      options={regionOptionList?.map((item) => ({
                        key: item.key,
                        text: item.text,
                      }))}
                      styles={comboStyles}
                    />
                  </React.Fragment>
                );
              case fieldTypes.COUNTRY:
                return (
                  <React.Fragment key={filterField.key}>
                    <ComboBox
                      placeholder="Any"
                      label={filterField.text}
                      selectedKey={getSelectedKeys(filterField.key)}
                      onChange={(_, item): void => {
                        onChange({ item, field: filterField.key });
                        if (removeFilterProvinceValue) {
                          removeFilterProvinceValue();
                        }
                        loadStateProvinceOptionsBySingularSelectedCountry(selectedCountries, item);
                      }}
                      multiSelect
                      disabled={countryRegionsLoadError.length > 0 || whetherOnlineOnlyOnSelected()}
                      autoComplete="on"
                      options={countryRegionOptionList?.map((item) => ({
                        key: item.key,
                        text: item.text,
                      }))}
                      styles={comboStyles}
                    />
                  </React.Fragment>
                );
              case fieldTypes.PROVINCE:
                return (
                  <React.Fragment key={filterField.key}>
                    <ComboBox
                      placeholder="Any"
                      label={filterField.text}
                      selectedKey={getSelectedKeys(filterField.key)}
                      onChange={(_, item): void => {
                        onChange({ item, field: filterField.key });
                      }}
                      multiSelect
                      disabled={
                        stateProvincesLoadError.length > 0 || selectedCountries.length !== 1
                      }
                      autoComplete="on"
                      options={stateProvinceOptionList?.map((item) => ({
                        key: item.key,
                        text: item.text,
                      }))}
                      styles={comboStyles}
                    />
                  </React.Fragment>
                );
              case fieldTypes.INDUSTRY_FOCUS:
                return (
                  <React.Fragment key={filterField.key}>
                    <ComboBox
                      placeholder="Any"
                      label={filterField.text}
                      selectedKey={getSelectedKeys(filterField.key)}
                      onChange={(_, item): void => {
                        onChange({ item, field: filterField.key });
                      }}
                      multiSelect
                      disabled={!!industryFocusLoadError && industryFocusLoadError.length > 0}
                      autoComplete="on"
                      options={industryFocusOptionList?.map((item) => ({
                        key: item.key,
                        text: item.key,
                      }))}
                      styles={comboStyles}
                    />
                  </React.Fragment>
                );
              case fieldTypes.TECHNICAL_EXPERTISE:
                return (
                  <React.Fragment key={filterField.key}>
                    <ComboBox
                      placeholder="Any"
                      label={filterField.text}
                      selectedKey={getSelectedKeys(filterField.key)}
                      onChange={(_, item): void => {
                        onChange({ item, field: filterField.key });
                      }}
                      multiSelect
                      disabled={
                        !!technicalExpertiseLoadError && technicalExpertiseLoadError.length > 0
                      }
                      autoComplete="on"
                      options={technicalExpertiseOptionList?.map((item) => ({
                        key: item.key,
                        text: item.key,
                      }))}
                      styles={comboStyles}
                    />
                  </React.Fragment>
                );
              case fieldTypes.TECHNOLOGY_AREA:
                return (
                  <React.Fragment key={filterField.key}>
                    <ComboBox
                      placeholder="Any"
                      label={filterField.text}
                      selectedKey={getSelectedKeys(filterField.key)}
                      onChange={(_, item): void => {
                        onChange({ item, field: filterField.key });
                      }}
                      multiSelect
                      disabled={!!technologyAreaLoadError && technologyAreaLoadError.length > 0}
                      autoComplete="on"
                      options={technologyAreaOptionList}
                      styles={comboStyles}
                    />
                  </React.Fragment>
                );
              case fieldTypes.REVIEW_STATUS:
                const reviewStatusOptions: IDropdownOption[] = reviewStatusOptionList?.map(
                  (option) => ({
                    key: option.key,
                    text: option.text,
                  })
                ) as IDropdownOption[];

                return (
                  <React.Fragment key={filterField.key}>
                    <Dropdown
                      placeholder="Any"
                      label={filterField.text}
                      selectedKeys={getSelectedKeys(filterField.key)}
                      onChange={(_, item): void => {
                        onChange({ item, field: filterField.key });
                      }}
                      multiSelect
                      options={reviewStatusOptions}
                      styles={dropdownStyles}
                    />
                  </React.Fragment>
                );
              case fieldTypes.ACTIVITY_OR_EVENT_TYPE:
                return (
                  <React.Fragment key={filterField.key}>
                    <ComboBox
                      placeholder="Any"
                      label={filterField.text}
                      selectedKey={getSelectedKeys(filterField.key)}
                      onChange={(_, item): void => {
                        onChange({ item, field: filterField.key });
                      }}
                      multiSelect
                      disabled={!!activityEventTypesError}
                      autoComplete="on"
                      options={activityEventTypesOptionList}
                      styles={comboStyles}
                    />
                  </React.Fragment>
                );
              case fieldTypes.CL_REGION:
                return (
                  <React.Fragment key={filterField.key}>
                    <ComboBox
                      placeholder="Any"
                      label={filterField.text}
                      selectedKey={getSelectedKeys(filterField.key)}
                      onChange={(_, item): void => {
                        onChange({ item, field: filterField.key });
                      }}
                      multiSelect
                      disabled={!!clRegionsLoadError && clRegionsLoadError?.length > 0}
                      autoComplete="on"
                      options={clRegionsOptionList?.map((item) => ({
                        key: item.key,
                        text: item.key,
                      }))}
                      styles={comboStyles}
                    />
                  </React.Fragment>
                );
              case fieldTypes.DATE_PICKER:
                return (
                  <React.Fragment key={filterField.key}>
                    <DatePicker {...datePickerProps(filterField)} styles={datePickerStyles} />
                    <ClearDateText
                      onClick={(): void => {
                        removeFilterValue({
                          fieldName: filterField.key,
                          value: getSelectedKeys(filterField.key)[0],
                        });
                      }}
                    >
                      Clear
                    </ClearDateText>
                  </React.Fragment>
                );
              case fieldTypes.TECHNOLOGY_AREA_GROUP:
                return (
                  <React.Fragment key={filterField.key}>
                    <ComboBox
                      placeholder="Any"
                      label={filterField.text}
                      selectedKey={getSelectedKeys(filterField.key)}
                      onChange={(_, item): void => {
                        onChange({ item, field: filterField.key });
                      }}
                      multiSelect
                      disabled={false}
                      autoComplete="on"
                      options={technologyAreaGroupOptionList}
                      styles={comboStyles}
                    />
                  </React.Fragment>
                );
              case fieldTypes.AVAILABLE_CPM_LIST:
                return (
                  <React.Fragment key={filterField.key}>
                    <ComboBox
                      placeholder="Any"
                      label={filterField.text}
                      selectedKey={getSelectedKeys(filterField.key)}
                      onChange={(_, item): void => {
                        onChange({ item, field: filterField.key });
                      }}
                      multiSelect
                      disabled={false}
                      autoComplete="on"
                      options={availableCpmOptionList}
                      styles={comboStyles}
                    />
                  </React.Fragment>
                );
              default:
                return (
                  <React.Fragment key={filterField.key}>
                    <p>Field not yet defined</p>{" "}
                  </React.Fragment>
                );
            }
          })}
        </Stack>
      </div>
    </SideBarDiv>
  );
};

export default SideBar;
